import { createSlice } from '@reduxjs/toolkit';
import moment from 'moment';
import apolloClient from '../queries';
import {
  FetchFlights,
  FetchDirections,
} from '../queries/index.graphql';
import {
  AIRLINES,
  AIRLINES_QUERY_PARAMS_MAP,
  UNEXPECTED_ERROR_MESSAGE,
  BOOKING_CLASS_CLASS_J
} from '../constants';
import { sessionKey, setErrorMessages } from './app';
import {
  setDirections,
  setStayNights,
  setDirectionId,
  setPrefectureId,
  setAreaId,
  setDistrictId,
  setNumberOfRooms,
  setPeoplePerRoom,
  setPax,
} from './hotel';
import { apiClient } from './apiClient';
import {
  getFlightState,
  saveSelectedOutboundFlight,
  saveSelectedInboundFlight,
  saveSearchedFlightsInput,
  saveSelectedOutboundDate,
  saveSelectedInboundDate,
} from '../utils/localStorage';
import { isEqualFlightsInput } from '../utils/input';
import { filterAvailableFlights } from '../utils/search';
import { validateNumberOfRooms } from '../utils/roomAllocation';
import { findDefaultFlight, makeClassJFlight, parseFlightNameAna, isSameFlightNameByUrlParam } from '../utils/plain';
import { validateFlightListJson } from './hooks/flightList';

const defaultDayOffSet = 3;
const defaultStayNights = 1;
let presetOutboundFlightClass = undefined;
let presetInboundFlightClass = undefined;
let presetOutboundFlightNameFirst = undefined;
let presetOutboundFlightNameSecond = undefined;
let presetInboundFlightNameFirst = undefined;
let presetInboundFlightNameSecond = undefined;
const presetUnavailableErrorMessage = 'お選びいただいた航空便は満席または欠航となりました。予約操作を最初からやり直してください。';
const urlParamsErrorMessage = 'ご指定のプランはありません。';

const initialState = {
  fetchingOutbound: false,
  fetchingInbound: false,
  queryParamsValidated: false,
  airports: [],
  outboundFlights: [],
  inboundFlights: [],
  selectedOutboundFlight: undefined,
  selectedInboundFlight: undefined,
  selectedOutboundDate: undefined,
  selectedInboundDate: undefined,
  flightsInput: {
    airline: AIRLINES.jal,
    outbound: {
      departureAirportId: '25',
      arrivalAirportId: '39',
      includeConnectingFlight: true,
      date: moment().add(defaultDayOffSet, 'days').format('YYYY-MM-DD'),
    },
    inbound: {
      departureAirportId: '39',
      arrivalAirportId:'25',
      includeConnectingFlight: true,
      date: moment().add(defaultDayOffSet + defaultStayNights, 'days').format('YYYY-MM-DD'),
    },
    people: {
      adult: 2,
      childrenA: 0,
      childrenB: 0,
      childrenC: 0,
      children2: 0,
      infant: 0,
    },
  },
  searchTime: {
    outbound: '',
    inbound: '',
  },
  searchedFlightsInput: undefined,
  calenderMinDateOutbound: undefined,
  unavailableCarrierSelected: false,
};


const flightSlice = createSlice({
  name: 'flight',
  initialState,
  reducers: {
    setFetchingFlight: {
      reducer(state, action) {
        const { fetching } = action.payload;
        state.fetching = fetching;
      },
      prepare(fetching) {
        return { payload: { fetching } };
      }
    },
    setFetchingOutbound: {
      reducer(state, action) {
        const { fetching } = action.payload;
        state.fetchingOutbound = fetching;
      },
      prepare(fetching) {
        return { payload: { fetching } };
      }
    },
    setFetchingInbound: {
      reducer(state, action) {
        const { fetching } = action.payload;
        state.fetchingInbound = fetching;
      },
      prepare(fetching) {
        return { payload: { fetching } };
      }
    },
    setQueryParamsValidatedFlight: {
      reducer(state, action) {
        const { validated } = action.payload;
        state.queryParamsValidated = validated;
      },
      prepare(validated) {
        return { payload: { validated } };
      }
    },
    setAirports: {
      reducer(state, action) {
        const { airports } = action.payload;
        state.airports = airports;
      },
      prepare(airports) {
        return { payload: { airports } };
      }
    },
    setAirline: {
      reducer(state, action) {
        const { airline } = action.payload;
        state.flightsInput.airline = airline;
        state.searchedFlightsInput = state.flightsInput;
        saveSearchedFlightsInput(sessionKey, state.flightsInput);
      },
      prepare(airline) {
        return { payload: { airline } };
      }
    },
    setOutboundFlights: {
      reducer(state, action) {
        if (state.unavailableCarrierSelected) {
          return;
        }

        const { flights } = action.payload;
        state.outboundFlights = flights;

        if (!state.selectedOutboundFlight) {
          const filteredFlights = flights && flights.length ? filterAvailableFlights(flights): [];

          if (filteredFlights.length) {
            let newSelectedOutboundFlight = undefined;
            if (presetOutboundFlightNameFirst) {
              newSelectedOutboundFlight = !presetOutboundFlightNameSecond ? filteredFlights.find((f) => isSameFlightNameByUrlParam(f, presetOutboundFlightNameFirst))
                : filteredFlights.find((f) => {
                  const cf = f.connectingFlights && f.connectingFlights.length > 1 && f.connectingFlights[1];

                  if (!cf) {
                    return false;
                  }

                  return isSameFlightNameByUrlParam(f, presetOutboundFlightNameFirst) && isSameFlightNameByUrlParam(cf, presetOutboundFlightNameSecond);
                });

              if (newSelectedOutboundFlight && presetOutboundFlightClass === BOOKING_CLASS_CLASS_J) {
                newSelectedOutboundFlight = makeClassJFlight(newSelectedOutboundFlight);
              }
            }

            if (!newSelectedOutboundFlight) {
              newSelectedOutboundFlight = findDefaultFlight(filteredFlights);
            }

            state.selectedOutboundFlight = newSelectedOutboundFlight;
            saveSelectedOutboundFlight(sessionKey, newSelectedOutboundFlight);

            presetOutboundFlightNameFirst = undefined;
            presetOutboundFlightNameSecond = undefined;
          }
        }

        if (!state.selectedOutboundDate) {
          const newSelectedOutboundDate = state.flightsInput.outbound.date;
          state.selectedOutboundDate = newSelectedOutboundDate;
          saveSelectedOutboundDate(sessionKey, newSelectedOutboundDate);
        }

        state.searchedFlightsInput = state.flightsInput;
        saveSearchedFlightsInput(sessionKey, state.flightsInput);
      },
      prepare(flights) {
        return { payload: { flights } };
      }
    },
    setInboundFlights: {
      reducer(state, action) {
        if (state.unavailableCarrierSelected) {
          return;
        }

        const { flights } = action.payload;
        state.inboundFlights = flights;

        if (!state.selectedInboundFlight) {
          const filteredFlights = flights && flights.length ? filterAvailableFlights(flights): [];

          if (filteredFlights.length) {
            let newSelectedInboundFlight = undefined;
            if (presetInboundFlightNameFirst) {
              newSelectedInboundFlight = !presetInboundFlightNameSecond ? filteredFlights.find((f) => isSameFlightNameByUrlParam(f, presetInboundFlightNameFirst))
                : filteredFlights.find((f) => {
                  const cf = f.connectingFlights && f.connectingFlights.length > 1 && f.connectingFlights[1];

                  if (!cf) {
                    return false;
                  }

                  return isSameFlightNameByUrlParam(f, presetInboundFlightNameFirst) && isSameFlightNameByUrlParam(cf, presetInboundFlightNameSecond);
                });

              if (newSelectedInboundFlight && presetInboundFlightClass === BOOKING_CLASS_CLASS_J) {
                newSelectedInboundFlight = makeClassJFlight(newSelectedInboundFlight);
              }
            }

            if (!newSelectedInboundFlight) {
              newSelectedInboundFlight = findDefaultFlight(filteredFlights);
            }

            state.selectedInboundFlight = newSelectedInboundFlight;
            saveSelectedInboundFlight(sessionKey, newSelectedInboundFlight);

            presetInboundFlightNameFirst = undefined;
            presetInboundFlightNameSecond = undefined;
          }
        }

        if (!state.selectedInboundDate) {
          const newSelectedInboundDate = state.flightsInput.inbound.date;
          state.selectedInboundDate = newSelectedInboundDate;
          saveSelectedInboundDate(sessionKey, newSelectedInboundDate);
        }

        state.searchedFlightsInput = state.flightsInput;
        saveSearchedFlightsInput(sessionKey, state.flightsInput);
      },
      prepare(flights) {
        return { payload: { flights } };
      }
    },
    setSelectedOutboundFlight: {
      reducer(state, action) {
        const { flight } = action.payload;
        state.selectedOutboundFlight = flight;
        saveSelectedOutboundFlight(sessionKey, flight);
      },
      prepare(flight) {
        return { payload: { flight } };
      }
    },
    setSelectedInboundFlight: {
      reducer(state, action) {
        const { flight } = action.payload;
        state.selectedInboundFlight = flight;
        saveSelectedInboundFlight(sessionKey, flight);
      },
      prepare(flight) {
        return { payload: { flight } };
      }
    },
    setSelectedOutboundDate: {
      reducer(state, action) {
        const { selectedDate } = action.payload;
        state.selectedOutboundDate = selectedDate;
        saveSelectedOutboundDate(sessionKey, selectedDate);
      },
      prepare(selectedDate) {
        return { payload: { selectedDate } };
      }
    },
    setSelectedInboundDate: {
      reducer(state, action) {
        const { selectedDate } = action.payload;
        state.selectedInboundDate = selectedDate;
        saveSelectedInboundDate(sessionKey, selectedDate);
      },
      prepare(selectedDate) {
        return { payload: { selectedDate } };
      }
    },
    setAdult: {
      reducer(state, action) {
        const { adult } = action.payload;
        state.flightsInput.people.adult = adult;
      },
      prepare(adult) {
        return { payload: { adult } };
      }
    },
    setChildrenA: {
      reducer(state, action) {
        const { childrenA } = action.payload;
        state.flightsInput.people.childrenA = childrenA;
      },
      prepare(childrenA) {
        return { payload: { childrenA } };
      }
    },
    setChildrenB: {
      reducer(state, action) {
        const { childrenB } = action.payload;
        state.flightsInput.people.childrenB = childrenB;
      },
      prepare(childrenB) {
        return { payload: { childrenB } };
      }
    },
    setChildrenC: {
      reducer(state, action) {
        const { childrenC } = action.payload;
        state.flightsInput.people.childrenC = childrenC;
      },
      prepare(childrenC) {
        return { payload: { childrenC } };
      }
    },
    setChildren2: {
      reducer(state, action) {
        const { children2 } = action.payload;
        state.flightsInput.people.children2 = children2;
      },
      prepare(children2) {
        return { payload: { children2 } };
      }
    },
    setInfant: {
      reducer(state, action) {
        const { infant } = action.payload;
        state.flightsInput.people.infant = infant;
      },
      prepare(infant) {
        return { payload: { infant } };
      }
    },
    setOutboundDepartureAirportId: {
      reducer(state, action) {
        const { id } = action.payload;
        state.flightsInput.outbound.departureAirportId = id;
      },
      prepare(id) {
        return { payload: { id } };
      }
    },
    setOutboundArrivalAirportId: {
      reducer(state, action) {
        const { id } = action.payload;
        state.flightsInput.outbound.arrivalAirportId = id;
      },
      prepare(id) {
        return { payload: { id } };
      }
    },
    setOutboundIncludeConnectingFlight: {
      reducer(state, action) {
        const { includeConnectingFlight } = action.payload;
        state.flightsInput.outbound.includeConnectingFlight = includeConnectingFlight;
      },
      prepare(includeConnectingFlight) {
        return { payload: { includeConnectingFlight } };
      }
    },
    setOutboundDate: {
      reducer(state, action) {
        const { date } = action.payload;
        state.flightsInput.outbound.date = date;

        const inboundDate = moment(state.flightsInput.inbound.date);
        const newDate = moment(date);
        const isNotAfterError = !inboundDate.isAfter(newDate);
        const isExceededMaxStayNightsError = inboundDate.diff(newDate, 'days') > 7;
        if (isNotAfterError || isExceededMaxStayNightsError) {
          state.flightsInput.inbound.date = newDate.clone().add(1, 'days').format('YYYY-MM-DD');
        }
      },
      prepare(date) {
        return { payload: { date } };
      }
    },
    setOutboundDateWithoutValidation: {
      reducer(state, action) {
        const { date } = action.payload;
        state.flightsInput.outbound.date = date;
      },
      prepare(date) {
        return { payload: { date } };
      }
    },
    setInboundDepartureAirportId: {
      reducer(state, action) {
        const { id } = action.payload;
        state.flightsInput.inbound.departureAirportId = id;
      },
      prepare(id) {
        return { payload: { id } };
      }
    },
    setInboundArrivalAirportId: {
      reducer(state, action) {
        const { id } = action.payload;
        state.flightsInput.inbound.arrivalAirportId = id;
      },
      prepare(id) {
        return { payload: { id } };
      }
    },
    setInboundIncludeConnectingFlight: {
      reducer(state, action) {
        const { includeConnectingFlight } = action.payload;
        state.flightsInput.inbound.includeConnectingFlight = includeConnectingFlight;
      },
      prepare(includeConnectingFlight) {
        return { payload: { includeConnectingFlight } };
      }
    },
    setInboundDate: {
      reducer(state, action) {
        const { date } = action.payload;
        state.flightsInput.inbound.date = date;
      },
      prepare(date) {
        return { payload: { date } };
      }
    },
    setCalenderMinDateOutbound: {
      reducer(state, action) {
        const { calenderMinDateOutbound } = action.payload;
        state.calenderMinDateOutbound = calenderMinDateOutbound;
      },
      prepare(calenderMinDateOutbound) {
        return { payload: { calenderMinDateOutbound } };
      }
    },
    setSearchedFlightsInput: {
      reducer(state, action) {
        const { flightsInput } = action.payload;
        state.searchedFlightsInput = flightsInput || state.flightsInput;

        // ページロード時、まだsessionKeyがグローバルにセットされていないときは処理しない
        if (sessionKey) {
          saveSearchedFlightsInput(sessionKey, state.searchedFlightsInput);
        }
      },
      prepare(flightsInput) {
        return { payload: { flightsInput } };
      }
    },
    setFlightsInput: {
      reducer(state, action) {
        const { flightsInput } = action.payload;
        state.flightsInput = flightsInput || state.flightsInput;
      },
      prepare(flightsInput) {
        return { payload: { flightsInput } };
      }
    },
    setUnavailableCarrierSelected: {
      reducer(state, action) {
        const { unavailableCarrierSelected } = action.payload;
        state.unavailableCarrierSelected = unavailableCarrierSelected;
      },
      prepare(unavailableCarrierSelected) {
        return { payload: { unavailableCarrierSelected } };
      }
    },
    setOutboundSearchTime: {
      reducer(state, action) {
        const { outboundSearchTime } = action.payload;
        state.searchTime.outbound = outboundSearchTime;
      },
      prepare(outboundSearchTime) {
        return { payload: { outboundSearchTime } };
      }
    },
    setInboundSearchTime: {
      reducer(state, action) {
        const { inboundSearchTime } = action.payload;
        state.searchTime.inbound = inboundSearchTime;
      },
      prepare(inboundSearchTime) {
        return { payload: { inboundSearchTime } };
      }
    },
  },
});

export const {
  setFetchingOutbound,
  setFetchingInbound,
  setQueryParamsValidatedFlight,
  setAirports,
  setAirline,
  setOutboundFlights,
  setInboundFlights,
  setSelectedOutboundFlight,
  setSelectedInboundFlight,
  setSelectedOutboundDate,
  setSelectedInboundDate,
  setAdult,
  setChildrenA,
  setChildrenB,
  setChildrenC,
  setChildren2,
  setInfant,
  setOutboundDepartureAirportId,
  setOutboundArrivalAirportId,
  setOutboundIncludeConnectingFlight,
  setOutboundDate,
  setOutboundDateWithoutValidation,
  setInboundDepartureAirportId,
  setInboundArrivalAirportId,
  setInboundIncludeConnectingFlight,
  setInboundDate,
  setCalenderMinDateOutbound,
  setSearchedFlightsInput,
  setFlightsInput,
  setUnavailableCarrierSelected,
  setOutboundSearchTime,
  setInboundSearchTime
} = flightSlice.actions;

const createAirportsArray = (directions) => {
  return directions.reduce((pre, current) => {
    pre.push(...current.airports);
    return pre;
  }, []);
};

export const createPrefecturesArray = (directions) => {
  return directions.reduce((pre, current) => {
    pre.push(...current.prefectures);
    return pre;
  }, []);
};

export const fetchAirports = () => async (dispatch) => {
  try {
    const { data } = await apolloClient.query({ query: FetchDirections });
    const {
      directions,
    } = data;
    dispatch(setDirections(directions));

    const airports = createAirportsArray(directions);
    dispatch(setAirports(airports));

    return {
      directions,
      airports,
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error);
  }
};

const findAirportByCode = (airports, code) => {
  return airports.find((a) => a.code === code);
};

export const fetchEssentialFlightStateFromSession = (key) => (dispatch) => {
  const flightState = getFlightState(key);

  const {
    searchedFlightsInput, selectedOutboundFlight, selectedInboundFlight,
    selectedOutboundDate, selectedInboundDate,
  } = flightState;
  if (searchedFlightsInput) {
    dispatch(setSearchedFlightsInput(searchedFlightsInput));
    dispatch(setFlightsInput(searchedFlightsInput));
  }

  if (selectedOutboundFlight) {
    dispatch(setSelectedOutboundFlight(selectedOutboundFlight));
  }

  if (selectedInboundFlight) {
    dispatch(setSelectedInboundFlight(selectedInboundFlight));
  }

  if (selectedOutboundDate) {
    dispatch(setSelectedOutboundDate(selectedOutboundDate));
  }

  if (selectedInboundDate) {
    dispatch(setSelectedInboundDate(selectedInboundDate));
  }
};

export const handleQueryParamsFlight = (queryParams) => async (dispatch) => {

  // Airports

  const { airports, directions } = await dispatch(fetchAirports());
  const outboundDepartureAirport = findAirportByCode(airports, queryParams.OutDepAptCD);
  const outboundArrivalAirport = findAirportByCode(airports, queryParams.OutArrAptCD);
  const inboundDepartureAirport = findAirportByCode(airports, queryParams.InDepAptCD);
  const inboundArrivalAirport = findAirportByCode(airports, queryParams.IndArrAptCD);

  if (outboundDepartureAirport && outboundArrivalAirport
    && inboundDepartureAirport && inboundArrivalAirport) {

    dispatch(setOutboundDepartureAirportId(outboundDepartureAirport.id));
    dispatch(setOutboundArrivalAirportId(outboundArrivalAirport.id));
    dispatch(setInboundDepartureAirportId(inboundDepartureAirport.id));
    dispatch(setInboundArrivalAirportId(inboundArrivalAirport.id));

  } else if (outboundDepartureAirport && outboundArrivalAirport
    && inboundDepartureAirport) {

    dispatch(setOutboundDepartureAirportId(outboundDepartureAirport.id));
    dispatch(setOutboundArrivalAirportId(outboundArrivalAirport.id));
    dispatch(setInboundDepartureAirportId(inboundDepartureAirport.id));
    dispatch(setInboundArrivalAirportId(outboundDepartureAirport.id));

  } else if (outboundDepartureAirport && outboundArrivalAirport
    && inboundArrivalAirport) {

    dispatch(setOutboundDepartureAirportId(outboundDepartureAirport.id));
    dispatch(setOutboundArrivalAirportId(outboundArrivalAirport.id));
    dispatch(setInboundDepartureAirportId(outboundArrivalAirport.id));
    dispatch(setInboundArrivalAirportId(inboundArrivalAirport.id));

  } else if (outboundDepartureAirport && inboundDepartureAirport
    && inboundArrivalAirport) {

    dispatch(setOutboundDepartureAirportId(outboundDepartureAirport.id));
    dispatch(setOutboundArrivalAirportId(inboundDepartureAirport.id));
    dispatch(setInboundDepartureAirportId(inboundDepartureAirport.id));
    dispatch(setInboundArrivalAirportId(inboundArrivalAirport.id));

  } else if (outboundArrivalAirport && inboundDepartureAirport
    && inboundArrivalAirport) {

    dispatch(setOutboundDepartureAirportId(inboundArrivalAirport.id));
    dispatch(setOutboundArrivalAirportId(outboundArrivalAirport.id));
    dispatch(setInboundDepartureAirportId(inboundDepartureAirport.id));
    dispatch(setInboundArrivalAirportId(inboundArrivalAirport.id));

  } else if (outboundDepartureAirport && outboundArrivalAirport) {

    dispatch(setOutboundDepartureAirportId(outboundDepartureAirport.id));
    dispatch(setOutboundArrivalAirportId(outboundArrivalAirport.id));
    dispatch(setInboundDepartureAirportId(outboundArrivalAirport.id));
    dispatch(setInboundArrivalAirportId(outboundDepartureAirport.id));

  } else if (outboundDepartureAirport && inboundDepartureAirport) {

    dispatch(setOutboundDepartureAirportId(outboundDepartureAirport.id));
    dispatch(setOutboundArrivalAirportId(inboundDepartureAirport.id));
    dispatch(setInboundDepartureAirportId(inboundDepartureAirport.id));
    dispatch(setInboundArrivalAirportId(outboundDepartureAirport.id));

  } else if (outboundDepartureAirport && inboundArrivalAirport) {

    dispatch(setOutboundDepartureAirportId(outboundDepartureAirport.id));
    dispatch(setInboundArrivalAirportId(inboundArrivalAirport.id));

  } else if (outboundArrivalAirport && inboundDepartureAirport) {

    dispatch(setOutboundArrivalAirportId(outboundArrivalAirport.id));
    dispatch(setInboundDepartureAirportId(inboundDepartureAirport.id));

  } else if (outboundArrivalAirport && inboundArrivalAirport) {

    dispatch(setOutboundDepartureAirportId(inboundArrivalAirport.id));
    dispatch(setOutboundArrivalAirportId(outboundArrivalAirport.id));
    dispatch(setInboundDepartureAirportId(outboundArrivalAirport.id));
    dispatch(setInboundArrivalAirportId(inboundArrivalAirport.id));

  } else if (inboundDepartureAirport && inboundArrivalAirport) {

    dispatch(setOutboundDepartureAirportId(inboundArrivalAirport.id));
    dispatch(setOutboundArrivalAirportId(inboundDepartureAirport.id));
    dispatch(setInboundDepartureAirportId(inboundDepartureAirport.id));
    dispatch(setInboundArrivalAirportId(inboundArrivalAirport.id));

  } else if (outboundDepartureAirport) {

    dispatch(setOutboundDepartureAirportId(outboundDepartureAirport.id));
    dispatch(setInboundArrivalAirportId(outboundDepartureAirport.id));

  } else if (outboundArrivalAirport) {

    dispatch(setOutboundArrivalAirportId(outboundArrivalAirport.id));
    dispatch(setInboundDepartureAirportId(outboundArrivalAirport.id));

  } else if (inboundDepartureAirport) {

    dispatch(setOutboundArrivalAirportId(inboundDepartureAirport.id));
    dispatch(setInboundDepartureAirportId(inboundDepartureAirport.id));

  } else if (inboundArrivalAirport) {

    dispatch(setOutboundDepartureAirportId(inboundArrivalAirport.id));
    dispatch(setInboundArrivalAirportId(inboundArrivalAirport.id));

  }

  // Direction(Hotel)

  let directionSpecified = false;
  if (queryParams.Direction) {
    const direction = directions.find((d) => Number(d.id) === Number(queryParams.Direction));
    if (direction) {
      directionSpecified = true;
      dispatch(setDirectionId(direction.id));
      dispatch(setPrefectureId(undefined));
    }
  }

  // Prefecture(Hotel)

  const prefectures = createPrefecturesArray(directions);
  let pref = prefectures.find((p) => Number(p.id) === Number(queryParams.Pref));
  if (pref) {
    dispatch(setPrefectureId(pref.id));

    const { areas } = pref;
    const area = areas.find((a) => a.id === queryParams.Area);
    if (area) {
      dispatch(setAreaId(area.id));

      const { districts } = area;
      const district = districts.find((d) => d.id === queryParams.District);
      if (district) {
        dispatch(setDistrictId(district.id));
      }
    }
  }

  if (!directionSpecified && !pref) {
    if (outboundArrivalAirport) {
      pref = outboundArrivalAirport.prefecture;
    } else if (inboundDepartureAirport) {
      pref = inboundDepartureAirport.prefecture;
    }

    if (pref) {
      const direction = directions.find((d) => d.prefectures.some((p) => p.id === pref.id));
      dispatch(setDirectionId(direction.id));
      dispatch(setPrefectureId(pref.id));
    }
  }

  // Selected Flights

  const usePresetFlightName = (str) => {
    const [firstFlightName, secondFlightName] = str.split('-');
    return { firstFlightName, secondFlightName };
  };

  const outboundFlightName = queryParams.Outnumber;
  if (outboundFlightName) {
    const { firstFlightName, secondFlightName } = usePresetFlightName(outboundFlightName);
    presetOutboundFlightNameFirst = firstFlightName;
    presetOutboundFlightNameSecond = secondFlightName;
  }

  const inboundFlightName = queryParams.Innumber;
  if (inboundFlightName) {
    const { firstFlightName, secondFlightName } = usePresetFlightName(inboundFlightName);
    presetInboundFlightNameFirst = firstFlightName;
    presetInboundFlightNameSecond = secondFlightName;
  }

  const outboundFlightClass = queryParams.OutClass;
  if (outboundFlightClass) {
    presetOutboundFlightClass = outboundFlightClass;
  }

  const inboundFlightClass = queryParams.InClass;
  if (inboundFlightClass) {
    presetInboundFlightClass = inboundFlightClass;
  }

  // Airline

  let airline = AIRLINES_QUERY_PARAMS_MAP[queryParams.Carrier];
  let flightDisplayList = { Jal: true, Ana: true };
  try {
    const { data: flightListRes } = await apiClient.getFlightList();
    const flightListJson = flightListRes && flightListRes.flight_list;

    if (flightListJson) {
      if (validateFlightListJson(flightListJson)) {
        flightDisplayList = flightListJson.display;

        if (!airline) {
          const defaultCarrier = flightListJson.default;
          airline = AIRLINES_QUERY_PARAMS_MAP[defaultCarrier.toUpperCase()];
        }
      } else {
        dispatch(setUnavailableCarrierSelected(true));
      }
    }
  } catch (e) {
    // nothing to do
  }
  if (airline) {
    if (flightDisplayList[airline]) {
      dispatch(setAirline(airline));
    } else {
      dispatch(setUnavailableCarrierSelected(true));
    }
  } else if (parseFlightNameAna(outboundFlightName) || parseFlightNameAna(inboundFlightName)) {
    dispatch(setAirline(AIRLINES.ana));
  }

  // People

  const peopleState = {
    ...initialState.flightsInput.people,
  };

  const pax = parseInt(queryParams.Pax, 10);
  const isValidPax = !!pax && 1 <= pax && pax <= 6;

  const adult = parseInt(queryParams.Adult, 10);
  const isValidAdult = 1 <= adult && adult <= 6;
  if (isValidAdult) {
    dispatch(setAdult(adult));
    peopleState.adult = adult;
  }

  const childrenA = parseInt(queryParams.ChildrenA, 10);
  const isValidChildrenA = 0 <= childrenA && childrenA <= 5;
  if (isValidChildrenA) {
    dispatch(setChildrenA(childrenA));
    peopleState.childrenA = childrenA;
  }

  const childrenB = parseInt(queryParams.ChildrenB, 10);
  const isValidChildrenB = 0 <= childrenB && childrenB <= 5;
  if (isValidChildrenB) {
    dispatch(setChildrenB(childrenB));
    peopleState.childrenB = childrenB;
  }

  const childrenC = parseInt(queryParams.ChildrenC, 10);
  const isValidChildrenC = childrenC <= peopleState.adult;
  if (isValidChildrenC) {
    dispatch(setChildrenC(childrenC));
    peopleState.childrenC = childrenC;
  }

  const children2 = parseInt(queryParams.Children2, 10);
  const isValidChildren2 = children2 <= peopleState.adult;
  if (isValidChildren2) {
    dispatch(setChildren2(children2));
    peopleState.children2 = children2;
  }

  const infant = parseInt(queryParams.Infant, 10);
  const isValidInfant = infant <= peopleState.adult;
  if (isValidInfant) {
    dispatch(setInfant(infant));
    peopleState.infant = infant;
  }

  const numberOfRooms = parseInt(queryParams.NumberOfRooms, 10);
  if (!isValidAdult && !isValidChildrenA && !isValidChildrenB) {
    const _pax = isValidPax ? pax : 2;
    const _numberOfRooms = numberOfRooms || 1;
    const value = _pax * _numberOfRooms;
    dispatch(setAdult(value));
    peopleState.adult = value;
  }

  const {
    validatedNumberOfRooms,
    validatedPeoplePerRoom,
  } = validateNumberOfRooms(peopleState, numberOfRooms);
  dispatch(setNumberOfRooms(validatedNumberOfRooms));
  dispatch(setPeoplePerRoom(validatedPeoplePerRoom));

  // paxがurlから渡ってきた場合はその値をそのままサーバー側にわたす
  // https://gitlab.fqsv.net/dp-plus/front/-/issues/322#note_51216
  if (pax) {
    dispatch(setPax(pax));
  }

  // Connecting

  const outboundIncludeConnectingFlight = parseInt(queryParams.OutTransit, 10);
  if (outboundIncludeConnectingFlight === 1) {
    dispatch(setOutboundIncludeConnectingFlight(true));
  }

  const inboundIncludeConnectingFlight = parseInt(queryParams.InTransit, 10);
  if (inboundIncludeConnectingFlight === 1) {
    dispatch(setInboundIncludeConnectingFlight(true));
  }


  // Date

  let outboundDate = queryParams.Days ? moment(queryParams.Days) : queryParams.OutDay ? moment(queryParams.OutDay) : moment.invalid();
  let inboundDate = queryParams.InDay ? moment(queryParams.InDay) : moment.invalid();

  let stayNights = parseInt(queryParams.StayNights, 10);
  if (!(1 <= stayNights && stayNights <= 7)) {
    stayNights = undefined;
  }

  const dayOffset = parseInt(queryParams.DayOffset, 10);
  const isValidDayOffset = (typeof dayOffset === 'number' && dayOffset < 366);

  let validatedDayOffset = defaultDayOffSet;
  if (isValidDayOffset) {
    validatedDayOffset = dayOffset || defaultDayOffSet;
    const minDateOutbound = moment().add(validatedDayOffset, 'days').format('YYYY-MM-DD');
    dispatch(setCalenderMinDateOutbound(minDateOutbound));
  }

  const today = moment();
  const tomorrow = moment().add(1, 'days');

  // 行きは明日以降
  if (outboundDate.isValid() && !outboundDate.isAfter(today)) {
    outboundDate = moment.invalid();
  }

  // 帰りは明後日以降
  if (inboundDate.isValid() && !inboundDate.isAfter(tomorrow)) {
    inboundDate = moment.invalid();
  }

  // maxStaynights > 7 になるような日付は無効（帰りを無効化）
  if (outboundDate.isValid() && inboundDate.isValid()) {
    const maxStayNights = inboundDate.diff(outboundDate, 'days');
    if (maxStayNights > 7) {
      inboundDate = moment.invalid();
    }
  }

  // stayNightsが行きと帰りの間に収まっていなかったら無効
  if (outboundDate.isValid() && inboundDate.isValid() && stayNights) {
    const maxStayNights = inboundDate.diff(outboundDate, 'days');
    if (stayNights > maxStayNights) {
      stayNights = undefined;
    }
  }

  if (outboundDate.isValid() && inboundDate.isValid() && !inboundDate.isAfter(outboundDate)) {

    if (!stayNights) {
      stayNights = 1;
    }
    outboundDate = moment().add(validatedDayOffset, 'days');
    inboundDate = outboundDate.clone().add(stayNights, 'days');

  } else if (outboundDate.isValid() && inboundDate.isValid() && !stayNights) {

    stayNights = inboundDate.diff(outboundDate, 'days');

  } else if (outboundDate.isValid() && !inboundDate.isValid() && stayNights) {

    inboundDate = outboundDate.clone().add(stayNights, 'days');

  } else if (outboundDate.isValid() && !inboundDate.isValid() && !stayNights) {

    stayNights = 1;
    inboundDate = outboundDate.clone().add(stayNights, 'days');

  } else if (!outboundDate.isValid() && inboundDate.isValid() && stayNights) {

    outboundDate = inboundDate.clone().subtract(stayNights, 'days');

  } else if (!outboundDate.isValid() && inboundDate.isValid() && !stayNights) {

    stayNights = 1;
    outboundDate = inboundDate.clone().subtract(stayNights, 'days');

  } else if (!outboundDate.isValid() && !inboundDate.isValid() && stayNights) {

    outboundDate = moment().add(validatedDayOffset, 'days');
    inboundDate = outboundDate.clone().add(stayNights, 'days');

  }

  if (outboundDate.isValid()) {
    dispatch(setOutboundDate(outboundDate.format('YYYY-MM-DD')));
  }
  if (inboundDate.isValid()) {
    dispatch(setInboundDate(inboundDate.format('YYYY-MM-DD')));
  }

  if (stayNights) {
    dispatch(setStayNights(stayNights));
  }

  if (isValidDayOffset) {
    const initialStayNights = stayNights || defaultStayNights;

    if (!outboundDate.isValid()) {
      const outboundDateFromDayOffset = moment().add(validatedDayOffset, 'days').format('YYYY-MM-DD');
      dispatch(setOutboundDateWithoutValidation(outboundDateFromDayOffset));
      dispatch(setCalenderMinDateOutbound(outboundDateFromDayOffset));

      if (!inboundDate.isValid()) {
        dispatch(setInboundDate(moment().add(validatedDayOffset + initialStayNights, 'days').format('YYYY-MM-DD')));
      }
    }
  }

  dispatch(setQueryParamsValidatedFlight(true));
};

export const getFilghtInputValidationErrors = (flightsInput) => {
  const errorMessages = [];
  const { adtNumberOfUnits, chdNumberOfUnits } = flightsInput;
  const numberOfPeople = adtNumberOfUnits + chdNumberOfUnits;
  if (numberOfPeople > 6 || numberOfPeople < 1) {
    const peopleErrorMessage = '参加人数の合計は1名～6名となるように選択してください。';
    errorMessages.push(peopleErrorMessage);
  }

  return errorMessages;
};

const validateAvailableFlights = (flights) => {
  if (!flights) {
    return [];
  }

  const valid = flights.find((f) => f.seatStatus === 'Vacant');
  return valid ? flights : [];
};

let lastFlightsInputOutbound = undefined;

export const fetchOutboundFlights = (flightsInput, first, page, disableRefreshOnInputChange = false, onSuccess) => (dispatch) => {
  dispatch(setFetchingOutbound(true));

  if (!disableRefreshOnInputChange && !isEqualFlightsInput(lastFlightsInputOutbound, flightsInput)) {
    dispatch(setSelectedOutboundFlight(undefined));
    dispatch(setSelectedOutboundDate(undefined));
  }

  return apolloClient.query({
    query: FetchFlights,
    variables: {
      input: flightsInput,
      first,
      page,
    },
    fetchPolicy: 'no-cache',
  }).then(({data}) => {
    const { flightData: flights } = data.flights;
    const { searchTime } = data.flights;
    const validatedFlights = validateAvailableFlights(flights);

    // error handling query params
    if (presetOutboundFlightNameFirst) {
      const presetFlight = !presetOutboundFlightNameSecond ? validatedFlights.find((f) => isSameFlightNameByUrlParam(f, presetOutboundFlightNameFirst))
        : validatedFlights.find((f) => {
          const cf = f.connectingFlights && f.connectingFlights.length > 1 && f.connectingFlights[1];

          if (!cf) {
            return false;
          }

          return isSameFlightNameByUrlParam(f, presetOutboundFlightNameFirst) && isSameFlightNameByUrlParam(cf, presetOutboundFlightNameSecond);
        });
      const presetFlightAvailable = presetFlight && presetFlight.seatStatus === 'Vacant';
      if (!presetFlightAvailable) {
        dispatch(setErrorMessages([presetUnavailableErrorMessage]));
      }
    }

    dispatch(setOutboundFlights(validatedFlights));
    lastFlightsInputOutbound = { ...flightsInput };
    dispatch(setFetchingOutbound(false));
    dispatch(setOutboundSearchTime(searchTime));

    onSuccess && onSuccess();
  }).catch((error) => {
    dispatch(setErrorMessages([UNEXPECTED_ERROR_MESSAGE]));
    // eslint-disable-next-line no-console
    console.log(error);
    dispatch(setFetchingOutbound(false));
  });
};

let lastFlightsInputInbound = undefined;

export const fetchInboundFlights = (flightsInput, first, page,  disableRefreshOnInputChange = false, onSuccess) => (dispatch) => {
  dispatch(setFetchingInbound(true));

  if (!disableRefreshOnInputChange && !isEqualFlightsInput(lastFlightsInputInbound, flightsInput)) {
    dispatch(setSelectedInboundFlight(undefined));
    dispatch(setSelectedInboundDate(undefined));
  }

  return apolloClient.query({
    query: FetchFlights,
    variables: {
      input: flightsInput,
      first,
      page,
    },
    fetchPolicy: 'no-cache',
  }).then(({data}) => {
    const { flightData: flights } = data.flights;
    const { searchTime } = data.flights;
    const validatedFlights = validateAvailableFlights(flights);

    // error handling query params
    if (presetInboundFlightNameFirst) {
      const presetFlight = !presetInboundFlightNameSecond ? validatedFlights.find((f) => isSameFlightNameByUrlParam(f, presetInboundFlightNameFirst))
        : validatedFlights.find((f) => {
          const cf = f.connectingFlights && f.connectingFlights.length > 1 && f.connectingFlights[1];

          if (!cf) {
            return false;
          }

          return isSameFlightNameByUrlParam(f, presetInboundFlightNameFirst) && isSameFlightNameByUrlParam(cf, presetInboundFlightNameSecond);
        });
      const presetFlightAvailable = presetFlight && presetFlight.seatStatus === 'Vacant';
      if (!presetFlightAvailable) {
        dispatch(setErrorMessages([presetUnavailableErrorMessage]));
      }
    }

    dispatch(setInboundFlights(validatedFlights));
    lastFlightsInputInbound = { ...flightsInput };
    dispatch(setFetchingInbound(false));
    dispatch(setInboundSearchTime(searchTime));

    onSuccess && onSuccess();
  }).catch((error) => {
    dispatch(setErrorMessages([UNEXPECTED_ERROR_MESSAGE]));
    // eslint-disable-next-line no-console
    console.log(error);
    dispatch(setFetchingInbound(false));
  });
};

export default flightSlice.reducer;